import { useState, useEffect } from 'react'
import { FiPlus, FiTrash2, FiUsers, FiShield, FiBriefcase, FiUserPlus, FiUserMinus, FiEdit2, FiCheck, FiChevronDown, FiChevronRight, FiGlobe, FiSettings } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../utils/api'
import { useAuth } from '../App'

export default function Users() {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState([])
  const [groups, setGroups] = useState([])
  const [companies, setCompanies] = useState([])

  // Seçili şirket (null = tüm kullanıcılar)
  const [selectedCompany, setSelectedCompany] = useState(null)

  // Açık gruplar (accordion)
  const [expandedGroups, setExpandedGroups] = useState({})

  // Formlar
  const [showCompanyForm, setShowCompanyForm] = useState(false)
  const [showUserForm, setShowUserForm] = useState(false)
  const [showGroupForm, setShowGroupForm] = useState(false)
  const [companyForm, setCompanyForm] = useState({ name: '', description: '', max_users: 0 })
  const [userForm, setUserForm] = useState({ username: '', password: '', full_name: '', email: '', role: 'viewer', company_id: '' })
  const [groupForm, setGroupForm] = useState({ company_id: '', name: '', description: '', can_manage_members: false })

  // Düzenleme
  const [editingCompany, setEditingCompany] = useState(null)
  const [editingUser, setEditingUser] = useState(null)

  // Sayfa izinleri
  const [permTarget, setPermTarget] = useState(null) // { type: 'group'|'user', id, name }
  const [allPages, setAllPages] = useState([])
  const [permChecked, setPermChecked] = useState({}) // { page_id: true/false/null }

  // Üye ekleme
  const [addingMember, setAddingMember] = useState(null)
  const [memberUserId, setMemberUserId] = useState('')

  // Arama
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => { loadAll() }, [])

  const loadAll = () => { loadCompanies(); loadUsers(); loadGroups() }
  const loadCompanies = async () => {
    try { const { data } = await api.get('/users/companies'); setCompanies(data) } catch (e) { /* */ }
  }
  const loadUsers = async () => {
    try { const { data } = await api.get('/users'); setUsers(data) } catch (e) { /* */ }
  }
  const loadGroups = async () => {
    try { const { data } = await api.get('/users/groups'); setGroups(data) } catch (e) { /* */ }
  }

  // === CRUD ===
  const createCompany = async (e) => {
    e.preventDefault()
    try {
      const { data } = await api.post('/users/companies', companyForm)
      toast.success('Şirket oluşturuldu')
      setShowCompanyForm(false)
      setCompanyForm({ name: '', description: '' })
      loadCompanies()
      setSelectedCompany(data.id)
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata') }
  }
  const updateCompany = async (e) => {
    e.preventDefault()
    try {
      await api.put(`/users/companies/${editingCompany.id}`, {
        name: editingCompany.name,
        description: editingCompany.description,
        max_users: editingCompany.max_users,
        features: editingCompany.features || {},
      })
      toast.success('Şirket güncellendi')
      setEditingCompany(null)
      loadCompanies()
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata') }
  }
  const updateFeature = (key, value) => {
    setEditingCompany(prev => ({
      ...prev,
      features: { ...(prev.features || {}), [key]: value }
    }))
  }
  const updateFormatSetting = (key, value) => {
    setEditingCompany(prev => ({
      ...prev,
      features: {
        ...(prev.features || {}),
        number_format: { ...((prev.features || {}).number_format || {}), [key]: value }
      }
    }))
  }
  const deleteCompany = async (id) => {
    if (!confirm('Bu şirket ve tüm grupları/kullanıcıları silinecek!')) return
    try { await api.delete(`/users/companies/${id}`); toast.success('Silindi'); setSelectedCompany(null); loadAll() } catch (e) { toast.error('Silinemedi') }
  }
  const createUser = async (e) => {
    e.preventDefault()
    try {
      const payload = { ...userForm }
      if (payload.company_id) payload.company_id = parseInt(payload.company_id)
      else delete payload.company_id
      await api.post('/users', payload)
      toast.success('Kullanıcı oluşturuldu')
      setShowUserForm(false)
      setUserForm({ username: '', password: '', full_name: '', email: '', role: 'viewer', company_id: '' })
      loadAll()
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata') }
  }
  const updateUser = async (e) => {
    e.preventDefault()
    try {
      const payload = { email: editingUser.email, full_name: editingUser.full_name, role: editingUser.role, is_active: editingUser.is_active }
      if (editingUser.company_id) payload.company_id = parseInt(editingUser.company_id)
      if (editingUser.password) payload.password = editingUser.password
      await api.put(`/users/${editingUser.id}`, payload)
      toast.success('Kullanıcı güncellendi')
      setEditingUser(null)
      loadAll()
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata') }
  }
  const deleteUser = async (id) => {
    if (!confirm('Kullanıcı silinecek!')) return
    try { await api.delete(`/users/${id}`); toast.success('Silindi'); loadAll() } catch (e) { toast.error('Silinemedi') }
  }
  const createGroup = async (e) => {
    e.preventDefault()
    try {
      await api.post('/users/groups', { ...groupForm, company_id: parseInt(groupForm.company_id) })
      toast.success('Grup oluşturuldu')
      setShowGroupForm(false)
      setGroupForm({ company_id: '', name: '', description: '', can_manage_members: false })
      loadGroups()
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata') }
  }
  const deleteGroup = async (id) => {
    if (!confirm('Grup silinecek!')) return
    try { await api.delete(`/users/groups/${id}`); toast.success('Silindi'); loadGroups() } catch (e) { toast.error('Silinemedi') }
  }
  const addMember = async (groupId) => {
    if (!memberUserId) return
    try {
      await api.post(`/users/groups/${groupId}/users/${memberUserId}`)
      toast.success('Üye eklendi')
      setAddingMember(null)
      setMemberUserId('')
      loadAll()
    } catch (err) { toast.error(err.response?.data?.detail || 'Eklenemedi') }
  }
  const removeMember = async (groupId, userId) => {
    try {
      await api.delete(`/users/groups/${groupId}/users/${userId}`)
      toast.success('Üye çıkarıldı')
      loadAll()
    } catch (err) { toast.error('Çıkarılamadı') }
  }

  const toggleGroup = (gid) => setExpandedGroups(prev => ({ ...prev, [gid]: !prev[gid] }))

  // === Sayfa İzinleri ===
  const openGroupPerms = async (group) => {
    try {
      const [pagesRes, permsRes] = await Promise.all([
        api.get('/users/pages'),
        api.get(`/users/groups/${group.id}/permissions`),
      ])
      setAllPages(pagesRes.data)
      const checked = {}
      permsRes.data.forEach(p => { checked[p.page_id] = true })
      setPermChecked(checked)
      setPermTarget({ type: 'group', id: group.id, name: group.name })
    } catch (err) { console.error('GroupPerms error:', err); toast.error('Sayfa izinleri yüklenemedi: ' + (err.response?.data?.detail || err.message)) }
  }
  const openUserPerms = async (user) => {
    try {
      const [pagesRes, permsRes] = await Promise.all([
        api.get('/users/pages'),
        api.get(`/users/${user.id}/permissions`),
      ])
      setAllPages(pagesRes.data)
      const checked = {}
      permsRes.data.forEach(p => {
        if (p.user_override !== null) checked[p.page_id] = p.user_override
        else checked[p.page_id] = p.from_group ? 'group' : false
      })
      setPermChecked(checked)
      setPermTarget({ type: 'user', id: user.id, name: user.full_name || user.username, permsData: permsRes.data })
    } catch (err) { console.error('UserPerms error:', err); toast.error('Sayfa izinleri yüklenemedi: ' + (err.response?.data?.detail || err.message)) }
  }
  const savePerms = async () => {
    try {
      if (permTarget.type === 'group') {
        const page_ids = Object.entries(permChecked).filter(([, v]) => v === true).map(([k]) => parseInt(k))
        await api.post('/users/groups/permissions', { group_id: permTarget.id, page_ids })
      } else {
        const page_permissions = Object.entries(permChecked).map(([k, v]) => ({
          page_id: parseInt(k),
          can_view: v === 'group' ? null : v === true ? true : v === false ? false : null,
        })).filter(p => p.can_view !== null)
        await api.post(`/users/${permTarget.id}/permissions`, { user_id: permTarget.id, page_permissions })
      }
      toast.success('Sayfa izinleri kaydedildi')
      setPermTarget(null)
      loadAll()
    } catch (err) { toast.error('Kaydetme başarısız') }
  }

  const ROLE_LABELS = { super_admin: 'Super Admin', company_admin: 'Şirket Admin', viewer: 'Görüntüleyici' }
  const ROLE_COLORS = { super_admin: 'bg-red-100 text-red-700', company_admin: 'bg-purple-100 text-purple-700', viewer: 'bg-gray-100 text-gray-600' }

  // Filtrelenmiş veriler
  const selectedCompanyObj = companies.find(c => c.id === selectedCompany)
  const companyFilteredUsers = selectedCompany ? users.filter(u => u.company_id === selectedCompany) : users
  const filteredUsers = searchQuery
    ? companyFilteredUsers.filter(u =>
        (u.full_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : companyFilteredUsers
  const filteredGroups = selectedCompany ? groups.filter(g => g.company_id === selectedCompany) : groups

  return (
    <div className="h-full flex">
      {/* === SOL PANEL — Şirket Listesi === */}
      <aside className="w-64 bg-white border-r flex flex-col flex-shrink-0">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-gray-800 text-sm">Organizasyon</h2>
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {/* Tümü */}
          <button
            onClick={() => setSelectedCompany(null)}
            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${!selectedCompany ? 'bg-primary-50 text-primary-700 font-medium border-r-2 border-primary-600' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            <FiGlobe className="flex-shrink-0" />
            <span>Tüm Kullanıcılar</span>
            <span className="ml-auto text-xs text-gray-400">{users.length}</span>
          </button>

          {/* Şirketler */}
          <div className="mt-2 px-4 mb-1">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Şirketler</span>
          </div>
          {companies.map(c => (
            <button
              key={c.id}
              onClick={() => setSelectedCompany(c.id)}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${selectedCompany === c.id ? 'bg-primary-50 text-primary-700 font-medium border-r-2 border-primary-600' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <FiBriefcase className="flex-shrink-0" />
              <span className="truncate">{c.name}</span>
              <span className="ml-auto text-[10px] text-gray-400">
                {c.user_count}{c.max_users > 0 ? `/${c.max_users}` : ''}
              </span>
            </button>
          ))}

          {companies.length === 0 && (
            <p className="px-4 py-3 text-xs text-gray-300 italic">Henüz şirket yok</p>
          )}
        </nav>

        {/* Yeni Şirket */}
        {currentUser?.is_super_admin && (
          <div className="p-3 border-t">
            <button onClick={() => setShowCompanyForm(true)} className="w-full flex items-center justify-center gap-1 text-xs text-primary-600 hover:text-primary-700 py-2 border border-dashed border-primary-300 rounded-lg hover:bg-primary-50 transition-colors">
              <FiPlus size={12} /> Yeni Şirket
            </button>
          </div>
        )}
      </aside>

      {/* === SAĞ PANEL — Detay === */}
      <main className="flex-1 overflow-y-auto bg-gray-50">
        {/* Üst Bar */}
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              {selectedCompanyObj ? selectedCompanyObj.name : 'Tüm Kullanıcılar'}
            </h1>
            {selectedCompanyObj?.description && (
              <p className="text-xs text-gray-400 mt-0.5">{selectedCompanyObj.description}</p>
            )}
            <div className="flex gap-3 mt-1 text-xs text-gray-400">
              <span>{filteredUsers.length} kullanıcı{selectedCompanyObj?.max_users > 0 ? ` / ${selectedCompanyObj.max_users} limit` : ''}</span>
              <span>{filteredGroups.length} grup</span>
            </div>
          </div>
          <div className="flex gap-2">
            {selectedCompany && (
              <>
                <button onClick={() => setEditingCompany({ id: selectedCompanyObj.id, name: selectedCompanyObj.name, description: selectedCompanyObj.description || '', max_users: selectedCompanyObj.max_users || 0 })} className="text-xs px-3 py-1.5 border rounded-lg text-gray-500 hover:bg-gray-50 flex items-center gap-1">
                  <FiEdit2 size={11} /> Şirketi Düzenle
                </button>
                <button onClick={() => { setGroupForm({ ...groupForm, company_id: String(selectedCompany) }); setShowGroupForm(true) }} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1">
                  <FiPlus size={11} /> Grup Ekle
                </button>
              </>
            )}
            <button onClick={() => { setUserForm({ ...userForm, company_id: selectedCompany ? String(selectedCompany) : '' }); setShowUserForm(true) }} className="text-xs px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-1">
              <FiPlus size={11} /> Kullanıcı Ekle
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* === GRUPLAR (sadece şirket seçiliyse) === */}
          {selectedCompany && filteredGroups.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <FiShield size={12} /> Gruplar
              </h2>
              <div className="space-y-2">
                {filteredGroups.map(g => {
                  const isExpanded = expandedGroups[g.id]
                  const groupUsers = users.filter(u => g.users.some(gu => gu.id === u.id))
                  return (
                    <div key={g.id} className="bg-white rounded-xl border overflow-hidden">
                      {/* Grup başlığı */}
                      <div
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleGroup(g.id)}
                      >
                        {isExpanded ? <FiChevronDown size={14} className="text-gray-400" /> : <FiChevronRight size={14} className="text-gray-400" />}
                        <div className="flex-1">
                          <span className="font-medium text-gray-800 text-sm">{g.name}</span>
                          {g.description && <span className="text-xs text-gray-400 ml-2">{g.description}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {g.can_manage_members && (
                            <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">Üye yönetebilir</span>
                          )}
                          <span className="text-xs text-gray-400">{g.users.length} üye</span>
                          <span className="text-xs text-gray-400">{g.page_count} sayfa</span>
                          <button onClick={(e) => { e.stopPropagation(); openGroupPerms(g) }} className="p-1 text-gray-300 hover:text-green-600" title="Sayfa İzinleri">
                            <FiSettings size={13} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setAddingMember(addingMember === g.id ? null : g.id); setMemberUserId('') }} className="p-1 text-gray-300 hover:text-primary-600">
                            <FiUserPlus size={13} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteGroup(g.id) }} className="p-1 text-gray-300 hover:text-red-500">
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </div>

                      {/* Üye ekleme */}
                      {addingMember === g.id && (
                        <div className="px-4 py-2 bg-blue-50 border-t flex gap-2 items-center">
                          <select value={memberUserId} onChange={(e) => setMemberUserId(e.target.value)} className="flex-1 text-xs border rounded px-2 py-1.5 bg-white">
                            <option value="">Kullanıcı seçin...</option>
                            {users.filter(u => u.company_id === g.company_id && !g.users.some(gu => gu.id === u.id)).map(u => (
                              <option key={u.id} value={u.id}>{u.full_name || u.username} (ID: {u.id})</option>
                            ))}
                          </select>
                          <button onClick={() => addMember(g.id)} className="text-xs bg-primary-600 text-white px-3 py-1.5 rounded hover:bg-primary-700">Ekle</button>
                          <button onClick={() => setAddingMember(null)} className="text-xs text-gray-500 px-2 py-1.5">İptal</button>
                        </div>
                      )}

                      {/* Üye listesi */}
                      {isExpanded && (
                        <div className="border-t">
                          {g.users.length === 0 ? (
                            <p className="px-4 py-3 text-xs text-gray-300 italic">Bu grupta henüz üye yok</p>
                          ) : (
                            g.users.map((u, i) => {
                              const fullUser = users.find(fu => fu.id === u.id)
                              return (
                                <div key={u.id} className={`flex items-center gap-3 px-4 py-2 ${i > 0 ? 'border-t border-gray-50' : ''} hover:bg-gray-50`}>
                                  <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-500">
                                    {(u.full_name || u.username || '?')[0].toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm text-gray-800">{u.full_name || u.username}</span>
                                    {fullUser && <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${ROLE_COLORS[fullUser.role]}`}>{ROLE_LABELS[fullUser.role]}</span>}
                                  </div>
                                  <button onClick={() => removeMember(g.id, u.id)} className="p-1 text-gray-300 hover:text-red-500" title="Gruptan çıkar">
                                    <FiUserMinus size={13} />
                                  </button>
                                </div>
                              )
                            })
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {selectedCompany && filteredGroups.length === 0 && (
            <div className="bg-white rounded-xl border border-dashed p-6 text-center">
              <FiShield className="mx-auto text-gray-300 mb-2" size={24} />
              <p className="text-sm text-gray-400">Bu şirkette henüz grup yok</p>
              <button onClick={() => { setGroupForm({ ...groupForm, company_id: String(selectedCompany) }); setShowGroupForm(true) }} className="mt-2 text-xs text-primary-600 hover:text-primary-700">
                <FiPlus className="inline" size={11} /> İlk grubu oluştur
              </button>
            </div>
          )}

          {/* === KULLANICILAR === */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <FiUsers size={12} /> {selectedCompany ? 'Şirket Kullanıcıları' : 'Tüm Kullanıcılar'}
              </h2>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="İsim, kullanıcı adı veya email ile ara..."
                className="px-3 py-1.5 border rounded-lg text-sm w-72 focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>
            {filteredUsers.length > 0 ? (
              <div className="bg-white rounded-xl border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Kullanıcı</th>
                      {!selectedCompany && <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Şirket</th>}
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Rol</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Gruplar</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Durum</th>
                      <th className="px-4 py-2.5 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-primary-50 rounded-full flex items-center justify-center text-xs font-semibold text-primary-600">
                              {(u.full_name || u.username || '?')[0].toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-gray-800">{u.full_name || u.username}</div>
                              <div className="text-xs text-gray-400">{u.username}{u.email ? ` · ${u.email}` : ''}</div>
                            </div>
                          </div>
                        </td>
                        {!selectedCompany && (
                          <td className="px-4 py-2.5">
                            {u.company_name ? (
                              <button onClick={() => setSelectedCompany(u.company_id)} className="text-xs text-primary-600 hover:text-primary-700">
                                {u.company_name}
                              </button>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </td>
                        )}
                        <td className="px-4 py-2.5">
                          <span className={`text-xs px-2 py-0.5 rounded ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}>
                            {ROLE_LABELS[u.role] || u.role}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          {u.groups?.length > 0 ? u.groups.map((g) => (
                            <span key={g.id} className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded mr-1">{g.name}</span>
                          )) : <span className="text-xs text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1 text-xs ${u.is_active ? 'text-green-600' : 'text-red-400'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
                            {u.is_active ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex gap-1">
                            <button onClick={() => openUserPerms(u)} className="p-1 text-gray-300 hover:text-green-600" title="Sayfa İzinleri"><FiSettings size={13} /></button>
                            <button onClick={() => setEditingUser({ id: u.id, username: u.username, email: u.email || '', full_name: u.full_name || '', role: u.role, company_id: u.company_id || '', is_active: u.is_active, password: '' })} className="p-1 text-gray-300 hover:text-primary-600"><FiEdit2 size={13} /></button>
                            <button onClick={() => deleteUser(u.id)} className="p-1 text-gray-300 hover:text-red-500"><FiTrash2 size={13} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-dashed p-6 text-center">
                <FiUsers className="mx-auto text-gray-300 mb-2" size={24} />
                <p className="text-sm text-gray-400">{selectedCompany ? 'Bu şirkette henüz kullanıcı yok' : 'Henüz kullanıcı yok'}</p>
              </div>
            )}
          </section>
        </div>
      </main>

      {/* === MODALLER === */}

      {/* Şirket Oluştur */}
      {showCompanyForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCompanyForm(false)}>
          <form onSubmit={createCompany} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-semibold mb-4">Yeni Şirket</h2>
            <input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} placeholder="Şirket adı" className="w-full px-3 py-2 border rounded-lg text-sm mb-3" required />
            <textarea value={companyForm.description} onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })} placeholder="Açıklama" className="w-full px-3 py-2 border rounded-lg text-sm mb-3" rows={2} />
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">Maks. Kullanıcı Sayısı <span className="text-gray-400">(0 = sınırsız)</span></label>
              <input type="number" min={0} value={companyForm.max_users} onChange={(e) => setCompanyForm({ ...companyForm, max_users: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowCompanyForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
              <button type="submit" className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Oluştur</button>
            </div>
          </form>
        </div>
      )}

      {/* Şirket Düzenle */}
      {editingCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8" onClick={() => setEditingCompany(null)}>
          <form onSubmit={updateCompany} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl">
            <h2 className="text-lg font-semibold mb-4">Şirket Düzenle</h2>
            <input value={editingCompany.name} onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })} placeholder="Şirket adı" className="w-full px-3 py-2 border rounded-lg text-sm mb-3" required />
            <textarea value={editingCompany.description} onChange={(e) => setEditingCompany({ ...editingCompany, description: e.target.value })} placeholder="Açıklama" className="w-full px-3 py-2 border rounded-lg text-sm mb-3" rows={2} />
            <div className="mb-4">
              <label className="block text-xs text-gray-600 mb-1">Maks. Kullanıcı Sayısı <span className="text-gray-400">(0 = sınırsız)</span></label>
              <input type="number" min={0} value={editingCompany.max_users} onChange={(e) => setEditingCompany({ ...editingCompany, max_users: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>

            {/* Modul Ayarlari */}
            <div className="border-t pt-3 mb-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Modüller</h3>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={editingCompany.features?.plasiyer_raporu || false}
                  onChange={(e) => updateFeature('plasiyer_raporu', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600" />
                Plasiyer Raporu
              </label>
            </div>

            {/* Sayi Format Ayarlari */}
            <div className="border-t pt-3 mb-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Sayı Formatı</h3>
              {(() => {
                const nf = editingCompany.features?.number_format || {}
                return (
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-0.5">Bin Ayracı</label>
                      <select value={nf.thousands || 'dot'} onChange={(e) => updateFormatSetting('thousands', e.target.value)}
                        className="w-full px-3 py-1.5 border rounded-lg text-sm">
                        <option value="dot">Nokta (1.234.567)</option>
                        <option value="comma">Virgül (1,234,567)</option>
                        <option value="space">Boşluk (1 234 567)</option>
                        <option value="none">Ayraç Yok (1234567)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-0.5">Ondalık Ayracı</label>
                      <select value={nf.decimal || 'comma'} onChange={(e) => updateFormatSetting('decimal', e.target.value)}
                        className="w-full px-3 py-1.5 border rounded-lg text-sm">
                        <option value="comma">Virgül (1.234,56)</option>
                        <option value="dot">Nokta (1,234.56)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-0.5">Ondalık Basamak</label>
                      <select value={nf.decimals ?? 2} onChange={(e) => updateFormatSetting('decimals', parseInt(e.target.value))}
                        className="w-full px-3 py-1.5 border rounded-lg text-sm">
                        <option value={0}>Küsürat Yok (1.234)</option>
                        <option value={1}>1 Basamak (1.234,5)</option>
                        <option value={2}>2 Basamak (1.234,56)</option>
                        <option value={3}>3 Basamak (1.234,567)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-0.5">Kısaltma</label>
                      <select value={nf.abbreviation || 'none'} onChange={(e) => updateFormatSetting('abbreviation', e.target.value)}
                        className="w-full px-3 py-1.5 border rounded-lg text-sm">
                        <option value="none">Kısaltma Yok (37.000.000)</option>
                        <option value="auto">Otomatik (37M / 11,1 Bin)</option>
                        <option value="thousands">Bin (37.000 Bin)</option>
                        <option value="millions">Milyon (37 Milyon)</option>
                      </select>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <span className="text-xs text-gray-400">Önizleme: </span>
                      <span className="text-sm font-bold text-gray-700">
                        {(() => {
                          const sample = 37045123.45
                          const fmt = editingCompany.features?.number_format || {}
                          const abbr = fmt.abbreviation || 'none'
                          const dec = fmt.decimals ?? 2
                          const tSep = fmt.thousands === 'comma' ? ',' : fmt.thousands === 'space' ? ' ' : fmt.thousands === 'none' ? '' : '.'
                          const dSep = fmt.decimal === 'dot' ? '.' : ','
                          let val = sample, suffix = ''
                          if (abbr === 'auto') { val = sample / 1e6; suffix = 'M' }
                          else if (abbr === 'thousands') { val = sample / 1e3; suffix = ' Bin' }
                          else if (abbr === 'millions') { val = sample / 1e6; suffix = ' Milyon' }
                          const parts = val.toFixed(dec).split('.')
                          const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, tSep)
                          return intPart + (dec > 0 ? dSep + parts[1] : '') + suffix
                        })()}
                      </span>
                    </div>
                  </div>
                )
              })()}
            </div>

            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setEditingCompany(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
              <button type="submit" className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Kaydet</button>
            </div>
          </form>
        </div>
      )}

      {/* Kullanıcı Oluştur */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowUserForm(false)}>
          <form onSubmit={createUser} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-semibold mb-4">Yeni Kullanıcı</h2>
            <div className="space-y-3">
              <input value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} placeholder="Kullanıcı adı" className="w-full px-3 py-2 border rounded-lg text-sm" required />
              <input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder="Şifre" className="w-full px-3 py-2 border rounded-lg text-sm" required />
              <input value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} placeholder="Ad Soyad" className="w-full px-3 py-2 border rounded-lg text-sm" />
              <input value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="Email" className="w-full px-3 py-2 border rounded-lg text-sm" />
              <select value={userForm.company_id} onChange={(e) => setUserForm({ ...userForm, company_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="">Şirket seçin</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm">
                {currentUser?.is_super_admin && <option value="super_admin">Super Admin</option>}
                <option value="company_admin">Şirket Admin</option>
                <option value="viewer">Görüntüleyici</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button type="button" onClick={() => setShowUserForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
              <button type="submit" className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Oluştur</button>
            </div>
          </form>
        </div>
      )}

      {/* Kullanıcı Düzenle */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingUser(null)}>
          <form onSubmit={updateUser} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-semibold mb-4">Kullanıcı Düzenle — {editingUser.username}</h2>
            <div className="space-y-3">
              <input value={editingUser.full_name} onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })} placeholder="Ad Soyad" className="w-full px-3 py-2 border rounded-lg text-sm" />
              <input value={editingUser.email} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} placeholder="Email" className="w-full px-3 py-2 border rounded-lg text-sm" />
              <select value={editingUser.company_id} onChange={(e) => setEditingUser({ ...editingUser, company_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="">Şirket yok</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={editingUser.role} onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm">
                {currentUser?.is_super_admin && <option value="super_admin">Super Admin</option>}
                <option value="company_admin">Şirket Admin</option>
                <option value="viewer">Görüntüleyici</option>
              </select>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editingUser.is_active} onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.checked })} />
                Aktif
              </label>
              <input type="password" value={editingUser.password} onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })} placeholder="Yeni şifre (boş bırakırsan değişmez)" className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button type="button" onClick={() => setEditingUser(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
              <button type="submit" className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Kaydet</button>
            </div>
          </form>
        </div>
      )}

      {/* Grup Oluştur */}
      {showGroupForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowGroupForm(false)}>
          <form onSubmit={createGroup} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-semibold mb-4">Yeni Grup</h2>
            <div className="space-y-3">
              <select value={groupForm.company_id} onChange={(e) => setGroupForm({ ...groupForm, company_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" required>
                <option value="">Şirket seçin</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="Grup adı (ör: Muhasebe)" className="w-full px-3 py-2 border rounded-lg text-sm" required />
              <textarea value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} placeholder="Açıklama" className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={groupForm.can_manage_members} onChange={(e) => setGroupForm({ ...groupForm, can_manage_members: e.target.checked })} />
                Grup üyeleri başka üye ekleyebilsin
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button type="button" onClick={() => setShowGroupForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
              <button type="submit" className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Oluştur</button>
            </div>
          </form>
        </div>
      )}

      {/* Sayfa İzinleri Modal */}
      {permTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setPermTarget(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[80vh] flex flex-col">
            <h2 className="text-lg font-semibold mb-1">Sayfa İzinleri</h2>
            <p className="text-sm text-gray-500 mb-4">
              {permTarget.type === 'group' ? `Grup: ${permTarget.name}` : `Kullanıcı: ${permTarget.name}`}
            </p>

            <div className="flex-1 overflow-y-auto space-y-1 mb-4">
              {allPages.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">Henüz dashboard sayfası yok. Önce bir dashboard oluşturun.</p>
              ) : (
                /* Dashboard bazlı grupla — dashboard adina basinca toplu sec/birak */
                [...new Set(allPages.map(p => p.dashboard_name))].map(dashName => {
                  const dashPages = allPages.filter(p => p.dashboard_name === dashName)
                  const allChecked = dashPages.every(p => {
                    const v = permChecked[p.page_id]
                    return v === true || v === 'group'
                  })
                  const someChecked = dashPages.some(p => {
                    const v = permChecked[p.page_id]
                    return v === true || v === 'group'
                  })
                  const toggleAll = () => {
                    const next = !allChecked
                    setPermChecked(prev => {
                      const upd = { ...prev }
                      dashPages.forEach(p => {
                        upd[p.page_id] = permTarget.type === 'group'
                          ? next
                          : (next ? true : false)
                      })
                      return upd
                    })
                  }
                  return (
                  <div key={dashName} className="mb-3">
                    <button onClick={toggleAll}
                      className="w-full flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase mb-1 px-1 py-1 hover:bg-primary-50 rounded transition-colors"
                      title="Tum sayfalari sec/birak">
                      <input type="checkbox" checked={allChecked} ref={el => { if (el) el.indeterminate = !allChecked && someChecked }} readOnly className="rounded" />
                      <span className="flex-1 text-left">{dashName}</span>
                      <span className="text-[10px] text-gray-400 normal-case">{dashPages.filter(p => permChecked[p.page_id] === true || permChecked[p.page_id] === 'group').length}/{dashPages.length}</span>
                    </button>
                    {dashPages.map(page => {
                      const val = permChecked[page.page_id]
                      const isGroupPerm = val === 'group'
                      const isChecked = val === true || isGroupPerm

                      return (
                        <label key={page.page_id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              setPermChecked(prev => ({
                                ...prev,
                                [page.page_id]: permTarget.type === 'group'
                                  ? e.target.checked
                                  : e.target.checked ? true : false
                              }))
                            }}
                            className="rounded"
                          />
                          <span className="text-sm text-gray-700 flex-1">{page.page_name}</span>
                          {permTarget.type === 'user' && isGroupPerm && (
                            <span className="text-[10px] bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded">gruptan</span>
                          )}
                          {permTarget.type === 'user' && val === true && !isGroupPerm && (
                            <span className="text-[10px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded">kullanıcıya özel</span>
                          )}
                        </label>
                      )
                    })}
                  </div>
                  )
                })
              )}
            </div>

            <div className="flex gap-2 justify-end pt-3 border-t">
              <button onClick={() => setPermTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
              <button onClick={savePerms} className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
